// // // src/components/Header.tsx
// src/components/Header.tsx
import Styles from '../../styles/common/common.module.css';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Navbar, Nav, Form, Button } from 'react-bootstrap';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled, { keyframes } from 'styled-components';
import { jwtDecode } from 'jwt-decode';
import { GetTokenFromIndexedDB } from '../../utils/indexedDBUtils/get-indexed-db';
import IsTokenExpired from '../../utils/isTokenExpiredFunction/is-token-expired';
import { DeleteTokenInIndexDB } from '../../utils/indexedDBUtils/delete-index-db';
import { useSelector } from 'react-redux';
import { HeaderProps } from '../../interfaces/common/funtions';
import { BiCartAdd } from "react-icons/bi";
import { useDispatch } from 'react-redux';
import { GetAddedAllCarts } from '../../data-access/apis/common/Carts';
import { AppDispatch } from '../../store';
import SearchBox from '../../component/common/SearchBox';

// Define the typing animation keyframes
const typing = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const blink = keyframes`
  from { border-right: 2px solid transparent; }
  to { border-right: 2px solid black; }
`;

// Define the bouncing effect keyframes
const bounce = keyframes`
   0% { transform: translateY(10px); } /* Start 20px down */
  50% { transform: translateY(-10px); } /* Move up by 20px */
  100% { transform: translateY(10px); } /* Move back down to 20px */
`;

// Styled component with continuous typing, blinking, and bouncing animation
const TypingText = styled.span`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  border-right: 2px solid #007403; /* Cursor color */
  color: #007403; /* Text color */
  font-size: 1em;
  font-family: 'Courier New', Courier, monospace;
  animation: 
    ${typing} 4s steps(40, end) infinite,
    ${blink} 0.75s step-end infinite,
    ${bounce} 8s ease-in-out infinite; /* Add bounce animation */
  width: 0; /* Keep width 0 for continuous typing effect */
  margin-top: 5%; /* Adjust this as needed */
`;

const headerVariants = {
  hidden: { opacity: 0, y: -50 },
  visible: { opacity: 1, y: 0 },
};

// const Header: React.FC = () => {
const Header: React.FC<HeaderProps> = ({ handleLogout, isAuthenticated, setIsAuthenticated, expanded, setExpanded }) => {
  const account = useSelector((state: any) => state.auth);
  const cartItem = useSelector((state: any) => state.cartItem);
  const cartCount = cartItem?.cartItems?.length || 0;
  const cartLoaded = Boolean(cartItem?.cartLoaded);
  const cartLoading = Boolean(cartItem?.loading);

  const [userToken, setUserToken] = useState<string>('')
  const location = useLocation();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();


  useEffect(() => {
    const checkToken = async () => {
      const token = await GetTokenFromIndexedDB('authToken');
      if (token) {
        const decodedToken = jwtDecode(token);
        const isTokenValid = !IsTokenExpired(decodedToken);

        setIsAuthenticated(isTokenValid);

        if (!isTokenValid) {
          await DeleteTokenInIndexDB('authToken');
          setIsAuthenticated(false);
        }
      }
    };

    // Initial check
    checkToken();

    // Set interval to check token every 7 days and 1 second ( 7 days and 1 second)
    const intervalId = setInterval(checkToken, 604801000);

    // Cleanup function to clear the interval when the component is unmounted
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.statusCode]); // Dependency array (e.g., `account.statusCode`)

  useEffect(() => {
    if (userToken) {
      setIsAuthenticated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleClose = () => {
    setExpanded(false);
  };

  const getAuthToken = async () => {
    const token = await GetTokenFromIndexedDB('authToken')
    setUserToken(token)
  }

  useEffect(() => {
    getAuthToken()
  }, [])

  const handleCartClick = async () => {
    // Fetch the cart only the first time the user opens the cart.
    // After that, Redux keeps the cart in memory and add/remove actions update it locally.
    if (!cartLoaded && !cartLoading) {
      const result = await dispatch(GetAddedAllCarts());

      if (GetAddedAllCarts.fulfilled.match(result)) {
        navigate('/get-all-carts');
      }

      return;
    }

    navigate('/get-all-carts');
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={headerVariants}
      transition={{ duration: 0.9, ease: 'easeOut' }}
    >
      <Navbar
        expand="lg"
        expanded={expanded}
        style={{ backgroundColor: '#ffbd59' }}
      >
        <Container>
          <Navbar.Brand as={NavLink} to="/">
            <LazyLoadImage
              src={`${process.env.PUBLIC_URL}/favicon.png`}
              width="50px"
              height="50px"
              className="d-inline-block align-top navbar-logo"
              alt="logo"
            />{' '}
            <TypingText className={Styles.typingTest}>Shopping Web</TypingText>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbarScroll" onClick={handleToggle} />
          <Navbar.Collapse id="navbarScroll">
            <Nav
              className="me-auto my-2 my-lg-0"
              style={{ maxHeight: '100px' }}
              navbarScroll
            >
              <Nav.Link
                as={NavLink}
                to="/"
                className={classnames(Styles.navLink, {
                  [Styles.active]: location.pathname === '/',
                })}
                onClick={handleClose}
              >
                Home
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/about-us"
                className={classnames(Styles.navLink, {
                  [Styles.active]: location.pathname === '/about-us',
                })}
                onClick={handleClose}
              >
                About Us
              </Nav.Link>
              <Nav.Link
                as={NavLink}
                to="/contact-us"
                className={classnames(Styles.navLink, {
                  [Styles.active]: location.pathname === '/contact-us',
                })}
                onClick={handleClose}
              >
                Contact Us
              </Nav.Link>
            </Nav>
            <div className="d-xl-none w-100 mb-2">
              <SearchBox />
            </div>
            {/* <Form className="d-flex">
              <Button className={Styles.signupBtn}>Search</Button>
              </Form> */}
            {/* {isAuthenticated ? (
              <>
                <SearchBox />
              </>
            ) : null} */}

            <div className="d-none d-xl-block me-3 flex-grow-1">
              <SearchBox />
            </div>
            <Form className="d-flex">
              {isAuthenticated ? (
                <>
                  <div
                    className="position-relative me-3"
                    onClick={handleCartClick}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open cart. ${cartCount} item${cartCount === 1 ? '' : 's'} in cart`}
                    title="Open cart"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        void handleCartClick();
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <BiCartAdd size={45} />
                    <span
                      className="position-absolute badge rounded-pill bg-warning text-dark"
                      style={{
                        fontSize: '0.8rem',
                        top: '2px',
                        left: '35px',
                      }}
                    >
                      {cartCount}
                    </span>
                  </div>
                  <Button className={Styles.logoutBtn} onClick={handleLogout}>
                    Logout
                    {/* Star SVGs */}
                    <div className={`${Styles.star} ${Styles.star1}`}></div>
                    <div className={`${Styles.star} ${Styles.star2}`}></div>
                    <div className={`${Styles.star} ${Styles.star3}`}></div>
                    <div className={`${Styles.star} ${Styles.star4}`}></div>
                    <div className={`${Styles.star} ${Styles.star5}`}></div>
                    <div className={`${Styles.star} ${Styles.star6}`}></div>
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth/sign-up" className={Styles.link}>
                    <Button className={Styles.signupBtn} onClick={handleClose}>
                      SignUp
                      {/* Star SVGs */}
                      <div className={`${Styles.star} ${Styles.star1}`}></div>
                      <div className={`${Styles.star} ${Styles.star2}`}></div>
                      <div className={`${Styles.star} ${Styles.star3}`}></div>
                      <div className={`${Styles.star} ${Styles.star4}`}></div>
                      <div className={`${Styles.star} ${Styles.star5}`}></div>
                      <div className={`${Styles.star} ${Styles.star6}`}></div>
                    </Button>
                  </Link>

                  <Link to="/auth/sign-in" className={Styles.link}>
                    <Button className={Styles.signinBtn} onClick={handleClose}>
                      SignIn
                      {/* Star SVGs */}
                      <div className={`${Styles.star} ${Styles.star1}`}></div>
                      <div className={`${Styles.star} ${Styles.star2}`}></div>
                      <div className={`${Styles.star} ${Styles.star3}`}></div>
                      <div className={`${Styles.star} ${Styles.star4}`}></div>
                      <div className={`${Styles.star} ${Styles.star5}`}></div>
                      <div className={`${Styles.star} ${Styles.star6}`}></div>
                    </Button>
                  </Link>
                </>
              )}
            </Form>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </motion.div>
  );
};

export default Header;
